<script>
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import SiteForm from '@/components/sites/SiteForm';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'EditSite',
  components: {
    DashboardLayout,
    SidebarLink,
    SidebarLinkGroup,
    SiteForm
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('Edit Site'),
      breadcrumbs: [],
      hasChangeNotification: false,
      invalid: [],
      isDeleting: false,
      isDisabled: false,
      isFavorite: false,
      isSaving: false,
      site: {},
      statusCode: null,
      testing: false
    };
  },
  beforeRouteLeave(to, from, next) {
    // Check for unsaved changes
    if (this.$refs.siteForm && this.$refs.siteForm.isDirty()) {
      this.$confirm(this.$t("Your changes haven't been saved. Are you sure you want to continue?"), {
        title: this.$t('Unsaved Changes'),
        confirmButtonText: this.$t('Continue'),
        cancelButtonText: this.$t('Cancel')
      }).then(() => next());
    } else {
      next();
    }
  },
  created() {
    // Fetch initial data
    Promise.all([
      this.$http.get(`/sites/${this.$route.params.siteId}`),
      new Promise(resolve => {
        this.$http
          .get(`/users/${this.$store.state.user.id}/favorites/${this.$route.params.siteId}`)
          .then(() => resolve(true))
          .catch(() => resolve(false));
      }),
      new Promise(resolve => {
        this.$http
          .get(`/users/${this.$store.state.user.id}/change-notifications/${this.$route.params.siteId}`)
          .then(() => resolve(true))
          .catch(() => resolve(false));
      })
    ])
      .then(res => {
        this.site = res[0].data.site;
        this.breadcrumbs = [
          { text: this.$t('Sites'), to: { name: 'Sites' } },
          { text: this.site.name, to: { name: 'Site', params: { siteId: this.site.id } } },
          { text: this.$t('Edit') }
        ];
        this.isFavorite = res[1];
        this.hasChangeNotification = res[2];
        this.statusCode = 200;

        // Can't edit demo sites
        if (this.site.isDemo) {
          this.$router.push({ name: 'Site', params: { siteId: this.site.id } });
          return;
        }

        this.$nextTick(() => {
          this.$refs.siteForm.setData(
            Object.assign({}, this.site, {
              isFavorite: this.isFavorite,
              hasChangeNotification: this.hasChangeNotification
            })
          );
          this.$refs.siteForm.resetCleanState();
        });
      })
      .catch(err => (this.statusCode = err.response.status));
  },
  methods: {
    // Cancels all changes and goes back to the sites page
    handleCancel() {
      this.$router.push({ name: 'Site', params: { siteId: this.site.id } });
    },

    // Deletes the site
    handleDelete() {
      this.$confirm(this.$t('Are you sure you want to do this?'), {
        title: this.$t('Delete'),
        confirmButtonText: this.$t('Delete This Site'),
        confirmButtonClass: 'el-button--danger',
        cancelButtonText: this.$t('Cancel'),
        type: 'danger'
      }).then(() => {
        this.isDeleting = true;
        this.$http
          .delete(`/sites/${this.site.id}`)
          .then(() => {
            this.$refs.siteForm.resetCleanState();
            this.$router.push({ name: 'Sites' });
          })
          .catch(err => {
            this.$notify({
              type: 'error',
              title: this.$t('Delete'),
              message: getErrorMessage(err.response.data.code)
            });
          })
          .finally(() => (this.isDeleting = false));

        return null;
      });
    },

    // Saves the site
    handleSave() {
      const data = this.$refs.siteForm.getData();
      this.isSaving = true;
      this.$http
        .put(`/sites/${this.site.id}`, data)
        .then(() => (this.invalid = []))
        .then(() => {
          // Toggle favorite state
          if (data.isFavorite !== this.isFavorite) {
            const method = data.isFavorite ? 'post' : 'delete';
            return new Promise(resolve => {
              this.$http[method](`/users/${this.$store.state.user.id}/favorites/${this.site.id}`)
                .then(() => resolve())
                .catch(() => resolve());
            });
          }
        })
        .then(() => {
          // Toggle change notification
          if (data.hasChangeNotification !== this.hasChangeNotification) {
            const method = data.hasChangeNotification ? 'post' : 'delete';
            return new Promise(resolve => {
              this.$http[method](`/users/${this.$store.state.user.id}/change-notifications/${this.site.id}`)
                .then(() => resolve())
                .catch(() => resolve());
            });
          }
        })
        .then(() => {
          this.$refs.siteForm.resetCleanState();
          this.$router.push({ name: 'Site', params: { siteId: this.site.id } });
        })
        .catch(err => {
          const data = err.response.data;
          this.invalid = Array.isArray(data.invalid) ? data.invalid : [];
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isSaving = false));
    },

    // Tests the connection
    testConnection() {
      this.testing = true;
      this.$refs.siteForm.testConnection().then(() => (this.testing = false));
    }
  }
};
</script>

<template>
  <dashboard-layout :status-code="statusCode" size="small">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Sidebar -->
    <template slot="sidebar">
      <!-- Actions -->
      <sidebar-link-group :title="$t('Actions')">
        <sidebar-link
          :text="$t('Test Connection')"
          :loading="testing"
          icon="el-icon-fa-plug"
          @click.native.prevent="testConnection"
        />
      </sidebar-link-group>
    </template>

    <!-- Site form -->
    <site-form
      ref="siteForm"
      :site-id="site.id"
      :deleting="isDeleting"
      :disabled="isDisabled"
      :invalid="invalid"
      :saving="isSaving"
      @delete="handleDelete"
      @cancel="handleCancel"
      @submit="handleSave"
    />
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

// No styles yet
</style>
